// @todo: Migrate to @wandb/components. For more details see:
// https://www.notion.so/wandbai/Migrating-components-207e2f5c7ef380129201f72a5770a8db
// eslint-disable-next-line wandb/no-deprecated-imports
import {Button} from '@wandb/weave/components';
import React from 'react';

import {ChartAggOption} from '../../../../generated/graphql';
import {calculateIsSettingEnabled} from '../../../../state/views/workspaceSettings/settingOverrideUtils';
import {ClearSettingsRows} from '../components/ClearSettingsRow';
import {ResetSettingButton} from '../components/ResetSettingButton';
import {SettingIndentedRow} from '../components/SettingIndentedRow';
import {SettingLabel} from '../components/SettingLabel';
import {SettingToggleRow} from '../components/SettingToggleRow';
import {useClearSettingRows} from '../hooks/useClearSettingRows';
import {useLinePlotSettingsContext} from './LinePlotSettingsContextProvider';
import {useLinePlotSettingsWithDefaults} from './utils/getLinePlotSettings';

export const LinePlotGroupingSetting = () => {
  return (
    <div className="flex flex-col gap-8">
      <RunsTableGroupingSetting />
      <SettingIndentedRow>
        <GroupAggregationSetting />
      </SettingIndentedRow>
    </div>
  );
};

export const RunsTableGroupingSetting = () => {
  const {
    clearSetting,
    displayLinePlotSettings,
    linePlotSettings,
    settingLevel,
    settingOverrideCounts,
    trackLinePlotSetting,
    updateLinePlotSettings,
  } = useLinePlotSettingsContext();
  const {useRunsTableGroupingInPanels} = useLinePlotSettingsWithDefaults(
    displayLinePlotSettings
  );

  const clearSettingRows = useClearSettingRows({
    numOverrides: settingOverrideCounts,
    settingKey: 'useRunsTableGroupingInPanels',
    settingLevel,
    clearSetting,
    emptySettings: {useRunsTableGroupingInPanels: undefined},
  });

  return (
    <div>
      <SettingToggleRow
        clearSetting={clearSetting}
        displayValue={useRunsTableGroupingInPanels}
        emptySettings={{useRunsTableGroupingInPanels: undefined}}
        label="Use grouping from the runs table in charts"
        onCheckedChange={(newValue: boolean) => {
          updateLinePlotSettings({
            useRunsTableGroupingInPanels: newValue,
          });
          trackLinePlotSetting(
            'useRunsTableGroupingInPanels',
            newValue.toString()
          );
        }}
        settingKey="useRunsTableGroupingInPanels"
        settingLevel={settingLevel}
        value={linePlotSettings?.useRunsTableGroupingInPanels}
      />
      <ClearSettingsRows rows={clearSettingRows} />
    </div>
  );
};

const GROUP_AGGREGATION_OPTIONS: {
  label: string;
  value: ChartAggOption;
}[] = [
  {label: 'Mean', value: ChartAggOption.Mean},
  {label: 'Min', value: ChartAggOption.Min},
  {label: 'Max', value: ChartAggOption.Max},
];

const GroupAggregationSetting = () => {
  const {
    clearSetting,
    displayLinePlotSettings,
    linePlotSettings,
    settingLevel,
    settingOverrideCounts,
    trackLinePlotSetting,
    updateLinePlotSettings,
  } = useLinePlotSettingsContext();
  const {groupAgg} = useLinePlotSettingsWithDefaults(displayLinePlotSettings);

  const clearSettingRows = useClearSettingRows({
    numOverrides: settingOverrideCounts,
    settingKey: 'groupAgg',
    settingLevel,
    clearSetting,
    emptySettings: {groupAgg: undefined},
  });

  return (
    <div>
      <div className="flex items-center justify-between">
        <SettingLabel text="Aggregation" className="ml-14 w-[87px]" />
        <div className="flex items-center gap-8">
          <div className="flex items-center gap-2">
            {GROUP_AGGREGATION_OPTIONS.map(option => (
              <Button
                key={option.value}
                active={groupAgg === option.value}
                variant="secondary"
                onClick={() => {
                  updateLinePlotSettings({groupAgg: option.value});
                  trackLinePlotSetting('groupAgg', option.value);
                }}>
                {option.label}
              </Button>
            ))}
          </div>
          <ResetSettingButton
            isEnabled={calculateIsSettingEnabled('groupAgg', linePlotSettings)}
            settingLevel={settingLevel}
            onButtonClick={() => {
              clearSetting(settingLevel, 'groupAgg', {groupAgg: undefined});
            }}
          />
        </div>
      </div>
      <ClearSettingsRows className="ml-14" rows={clearSettingRows} />
    </div>
  );
};
